import { useCallback, useEffect, useState } from "react";
import { api, errMsg, downloadExcel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";

export default function Trends() {
  const { options } = useAuth();
  const [analyte, setAnalyte] = useState("all");
  const [level, setLevel] = useState("all");
  const [data, setData] = useState({ points: [], stats: { n: 0, mean: 0, sd: 0, cv: 0 }, analytes: [] });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (analyte !== "all") params.analyte = analyte;
      if (level !== "all") params.level = level;
      const res = await api.get("/trends/qc", { params });
      setData(res.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [analyte, level]);

  useEffect(() => { load(); }, [load]);

  const { mean, sd } = data.stats;

  return (
    <div data-testid="trends-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Trend Analysis</h1>
          <p className="text-sm text-[#6B7280] mt-1">Levey-Jennings view of your internal QC runs with ±1SD / ±2SD / ±3SD limits.</p>
        </div>
        <Button variant="outline" onClick={() => downloadExcel("qc", "QC_Data")} data-testid="export-qc-btn">
          <Download className="h-4 w-4 mr-2 text-[#2E7D32]" /> Excel
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <div className="w-56">
          <Select value={analyte} onValueChange={setAnalyte}>
            <SelectTrigger data-testid="trend-analyte-select" className="bg-white"><SelectValue placeholder="Analyte" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Analytes</SelectItem>
              {(data.analytes?.length ? data.analytes : options.analyte || []).map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-56">
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="trend-level-select" className="bg-white"><SelectValue placeholder="QC Level" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Levels</SelectItem>
              {(options.qc_level || []).map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {[["Data Points", data.stats.n], ["Mean", data.stats.mean], ["SD", data.stats.sd], ["CV %", data.stats.cv]].map(([l, v]) => (
          <div key={l} className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid={`stat-${l.replace(/[^a-z]/gi, "").toLowerCase()}`}>
            <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">{l}</p>
            <p className="font-heading text-2xl font-extrabold tracking-tight text-[#111827] mt-2">{v}</p>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-[#E5E7EB] bg-white p-4 mt-4">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.points}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
              <Tooltip />
              <Legend />
              {data.stats.n > 1 && (
                <>
                  <ReferenceLine y={mean} stroke="#2E7D32" label={{ value: "Mean", fontSize: 10 }} />
                  <ReferenceLine y={mean + sd} stroke="#FCC419" strokeDasharray="4 4" label={{ value: "+1SD", fontSize: 10 }} />
                  <ReferenceLine y={mean - sd} stroke="#FCC419" strokeDasharray="4 4" label={{ value: "-1SD", fontSize: 10 }} />
                  <ReferenceLine y={mean + 2 * sd} stroke="#FCC419" strokeDasharray="2 6" label={{ value: "+2SD", fontSize: 10 }} />
                  <ReferenceLine y={mean - 2 * sd} stroke="#FCC419" strokeDasharray="2 6" label={{ value: "-2SD", fontSize: 10 }} />
                  <ReferenceLine y={mean + 3 * sd} stroke="#C92A2A" strokeDasharray="2 6" label={{ value: "+3SD", fontSize: 10 }} />
                  <ReferenceLine y={mean - 3 * sd} stroke="#C92A2A" strokeDasharray="2 6" label={{ value: "-3SD", fontSize: 10 }} />
                </>
              )}
              <Line type="monotone" dataKey="value" name="QC Result" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {data.stats.n === 0 && (
          <p className="text-sm text-[#6B7280] text-center py-4" data-testid="trends-empty">
            No numeric QC results found yet. Add entries in Quality Control to see trends.
          </p>
        )}
      </div>
    </div>
  );
}
